import { useState } from 'react';
import { create } from 'zustand';
import { Copy, MessageCircle } from 'lucide-react';
import { Button, Modal } from '@/components/ui';

/**
 * Davet bağlantısını gösteren pencere.
 *
 * E-posta ayarı yoksa (veya e-posta ulaşmazsa) danışan hesabını açamaz;
 * diyetisyen bağlantıyı buradan kopyalayıp WhatsApp/SMS ile iletir.
 * Sayfalar arası geçişte (oluştur → danışan dosyası) kaybolmasın diye durum
 * küçük bir global depoda tutulur.
 */
type InvitationState = {
  url: string | null;
  name: string;
  emailSent: boolean;
  show: (url: string, name: string, emailSent: boolean) => void;
  close: () => void;
};

export const useInvitationLink = create<InvitationState>((set) => ({
  url: null,
  name: '',
  emailSent: false,
  show: (url, name, emailSent) => set({ url, name, emailSent }),
  close: () => set({ url: null }),
}));

/** Yanıtta bağlantı varsa pencereyi açar. */
export function showInvitationLink(
  response: unknown,
  name: string,
): void {
  if (!response || typeof response !== 'object') return;
  const data = response as { invitationUrl?: unknown; invitationSent?: unknown; emailSent?: unknown };
  if (typeof data.invitationUrl !== 'string') return;
  const sent = data.invitationSent === true || data.emailSent === true;
  useInvitationLink.getState().show(data.invitationUrl, name, sent);
}

export function InvitationLinkDialog() {
  const { url, name, emailSent, close } = useInvitationLink();
  const [copied, setCopied] = useState(false);
  if (!url) return null;
  const greeting = name ? `Merhaba ${name.split(' ')[0]}, ` : 'Merhaba, ';
  const message = `${greeting}Forever Deluxe hesabını açmak için bu bağlantıya dokun: ${url}`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <Modal
      open
      onClose={() => {
        setCopied(false);
        close();
      }}
      title="Davet bağlantısı"
      footer={
        <>
          <Button variant="ghost" onClick={close}>
            Kapat
          </Button>
          <Button icon={Copy} onClick={() => void copy()}>
            {copied ? 'Kopyalandı' : 'Bağlantıyı kopyala'}
          </Button>
        </>
      }
    >
      <div className="fd-stack" style={{ gap: 'var(--fd-space-3)' }}>
        <p style={{ color: 'var(--fd-color-text-muted)' }}>
          {emailSent
            ? `${name || 'Danışana'} davet e-postası gönderildi. İsterseniz bağlantıyı ayrıca mesajla da iletebilirsiniz.`
            : `Davet e-postası gönderilemedi (e-posta ayarı yapılmamış olabilir). Hesabın açılması için bağlantıyı ${name || 'danışana'} WhatsApp veya SMS ile iletin.`}
        </p>
        <input
          className="fd-input"
          readOnly
          value={url}
          aria-label="Davet bağlantısı"
          onFocus={(e) => e.currentTarget.select()}
        />
        <a
          className="fd-btn fd-btn--secondary fd-btn--md"
          href={`https://wa.me/?text=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noreferrer"
        >
          <MessageCircle size={16} aria-hidden />
          WhatsApp ile gönder
        </a>
        <p style={{ fontSize: '0.8125rem', color: 'var(--fd-color-text-muted)' }}>
          Bağlantı süreli ve tek kullanımlıktır; yeniden gönderilirse eskisi geçersiz olur.
        </p>
      </div>
    </Modal>
  );
}
